<template>
  <div class="anaPage">
    <div class="riskHead">
      <h2>预警分析中心 <em style="background:#7C5CFF">ANALYSIS</em></h2>
      <p class="riskSub">跨域汇总全部预警，发现规律与结构性问题：趋势与同环比、帕累托（80/20）、维度 × 风险域热力矩阵、重复 / 顽疾识别与自动结论</p>
    </div>

    <!-- 指标条 8 卡 -->
    <div class="stripRow">
      <div class="stripCard" v-for="c in statCards" :key="c.k" :style="{ borderLeft: '4px solid ' + c.color }">
        <div class="k">{{ c.k }}</div>
        <div class="v" :style="{ color: c.color }">{{ c.v }}<small v-if="c.u">{{ c.u }}</small></div>
        <div class="d">{{ c.d }}</div>
      </div>
    </div>

    <!-- 图1：趋势与同环比 -->
    <div class="riskCard">
      <div class="cardHead"><h3>📈 趋势与同环比（近 12 个月）</h3><span class="badgeTag" style="background:rgba(124,92,255,.12);color:#7C5CFF">跨域汇总</span></div>
      <div class="momText" v-html="momText"></div>
      <div ref="trendChart" style="width:100%;height:280px"></div>
      <div class="tipText">紫色折线为全部预警；其余折线按风险域（用工合规 / 应收暂支未清 / 欠薪 / 社保）分线，分线之和 = 全部预警总数。</div>
    </div>

    <!-- 图2：帕累托 -->
    <div class="riskCard">
      <div class="cardHead"><h3>📊 帕累托（80 / 20）</h3><span class="badgeTag" style="background:rgba(255,159,28,.12);color:#FF9F1C">对象维度</span></div>
      <div class="tabRow">
        <div v-for="g in paretos" :key="g.dim" class="dimTab" :class="{ active: paretoDim === g.dim }" @click="paretoDim = g.dim">{{ g.dimName }}</div>
      </div>
      <div class="hbarList">
        <div v-for="(r, i) in paretoRows" :key="r.label" class="hbarRow">
          <div class="hbarLabel">{{ i + 1 }}. {{ r.label }}</div>
          <div class="hbarTrack"><div class="hbarFill" :style="{ width: hbarW(r) + '%', background: '#7C5CFF' }"></div></div>
          <div class="hbarVal">{{ r.count }} 项 · 累计 {{ r.cumPct }}%</div>
        </div>
        <div v-if="paretoRows.length === 0" class="emptyText">暂无数据</div>
      </div>
      <div class="tipText" style="margin-top:8px">贡献 80% 预警量的临界对象数：<b>{{ curPareto.criticalCount }}</b> 个（共 {{ (curPareto.rows || []).length }} 个对象，按{{ curPareto.dimName }}降序）。</div>
    </div>

    <!-- 图3：热力矩阵 -->
    <div class="riskCard">
      <div class="cardHead" style="flex-wrap:wrap;gap:10px">
        <h3>🔥 热力矩阵：维度 × 风险域</h3>
        <span class="tabRow" style="margin-left:auto;border:none;padding:0">
          <div v-for="g in heats" :key="g.dim" class="dimTab" :class="{ active: heatDim === g.dim }" @click="heatDim = g.dim">{{ g.dimName }}</div>
        </span>
        <span class="badgeTag" style="background:rgba(46,123,255,.12);color:#2E7BFF">色深 = 预警数</span>
      </div>
      <div class="tableWrap">
        <table class="heatTable" v-if="curHeat.rows && curHeat.rows.length">
          <thead>
            <tr><th>{{ curHeat.dimName }}</th><th v-for="d in DOMAIN_COLS" :key="d.key">{{ d.name }}</th><th>合计</th></tr>
          </thead>
          <tbody>
            <tr v-for="r in curHeat.rows" :key="r.label">
              <td class="heatLabel">{{ r.label }}</td>
              <td v-for="d in DOMAIN_COLS" :key="d.key" class="heatCell"
                :style="heatCellStyle(r[d.key], heatMax)" :title="r.label + ' · ' + d.name + '：' + r[d.key] + ' 项'">{{ r[d.key] || '' }}</td>
              <td class="heatTotal">{{ r.total }}</td>
            </tr>
          </tbody>
        </table>
        <div v-else class="emptyText">暂无数据</div>
      </div>
      <div class="tipText" style="margin-top:8px">图例：<span class="lgCell" style="background:rgba(124,92,255,.08)"></span> 少
        <span class="lgCell" style="background:rgba(124,92,255,.35);margin-left:6px"></span> 中
        <span class="lgCell" style="background:rgba(124,92,255,.75);margin-left:6px"></span> 多（深色 = 该维度该风险域预警越多）。</div>
    </div>

    <!-- 图4：重复 / 顽疾识别 -->
    <div class="riskCard">
      <div class="cardHead"><h3>♻️ 重复 / 顽疾识别（近 12 个月同一项目 ≥ 3 次）</h3>
        <span class="badgeTag" style="background:rgba(255,59,92,.12);color:#FF3B5C">{{ repeats.length }} 个对象</span></div>
      <div class="tableWrap">
        <el-table :data="repeats" border size="small" max-height="360">
          <el-table-column prop="label" label="对象（项目）" min-width="200" show-overflow-tooltip></el-table-column>
          <el-table-column prop="count" label="12 个月触发次数" width="140" align="center"></el-table-column>
          <el-table-column prop="domains" label="涉及风险域" min-width="220" show-overflow-tooltip></el-table-column>
          <el-table-column prop="lastMonth" label="最近触发月" width="110" align="center"></el-table-column>
        </el-table>
      </div>
      <h4 class="subTitle">共现关系（同一项目同时命中两类风险）</h4>
      <div class="pairList">
        <div v-for="p in pairs" :key="p.domainA + p.domainB" class="pairRow">
          <span>{{ p.nameA }} <b>↔</b> {{ p.nameB }}</span>
          <span>共现 <b>{{ p.projCount }}</b> 个项目（{{ p.pct }}%）</span>
        </div>
        <div v-if="pairs.length === 0" class="emptyText">暂无共现关系</div>
      </div>
    </div>

    <!-- 自动结论 -->
    <div class="riskCard" style="border-color:rgba(124,92,255,.35)">
      <div class="cardHead"><h3>🧠 自动结论清单</h3><span class="badgeTag" style="background:rgba(124,92,255,.12);color:#7C5CFF">数据自动生成</span></div>
      <div class="tipText" style="margin-bottom:8px">由当前预警数据自动归纳，仅供参考；结论随数据刷新而更新。</div>
      <ol class="conclList">
        <li v-for="(t, i) in conclusions" :key="i" v-html="t"></li>
      </ol>
    </div>
  </div>
</template>

<script>
import xz from "@/common";
import * as echarts from "echarts";

const DOMAIN_COLS = [
  { key: 'labor', name: '用工合规' },
  { key: 'ar', name: '应收暂支未清' },
  { key: 'payroll', name: '欠薪' },
  { key: 'social', name: '社保夯实率' }
];

export default {
  name: 'riskAnalysis',
  data() {
    return {
      a: null,                       // GetRiskAnalysis 返回
      paretoDim: 'project',
      heatDim: 'branch',
      trendChart: null,
      DOMAIN_COLS
    }
  },
  computed: {
    statCards() {
      const d = this.a || {}
      const f = (k, v) => this.pick(d, k, v)
      return [
        { k: '预警总量', v: f('totalCount', 0), u: '项', d: '近12月跨域全部预警', color: '#7C5CFF' },
        { k: '本月新增', v: f('monthNewCount', 0), u: '项', d: this.curMonthLabel() + ' 新触发', color: '#FF9F1C' },
        { k: '已核销', v: f('closedCount', 0), u: '项', d: '自动 + 人工核销', color: '#00A576' },
        { k: '存量', v: f('activeCount', 0), u: '项', d: '未闭环预警', color: '#FF3B5C' },
        { k: '平均停留时长', v: f('avgStayDays', 0), u: '天', d: '生成 → 核销 / 当前', color: '#2E7BFF' },
        { k: '未闭环率', v: f('openRate', 0), u: '%', d: '存量 / 总量', color: '#FF5FA2' },
        { k: '人工核销率', v: f('manualRate', 0), u: '%', d: '人工核销 / 总量', color: '#00C7E6' },
        { k: '风险指数', v: f('riskIndex', 0), u: '', d: 'Σ 预警值（蓝10/黄30/橙60/红90）', color: '#7C5CFF' }
      ]
    },
    momText() {
      if (!this.a || !this.a.months) return ''
      const cur = this.a.months[11] || '', prevC = this.a.prevMonthCount || 0, curC = this.a.monthNewCount || 0
      const mom = prevC ? Math.round((curC - prevC) / prevC * 1000) / 10 : null
      const lbl = m => m ? (m.slice(0, 4) + '-' + m.slice(4)) : ''
      return '本月（' + lbl(cur) + '）预警 <b>' + curC + '</b> 项；环比 ' +
        (mom === null ? '—' : ((mom >= 0 ? '+' : '') + mom + '%')) + '（上月 ' + prevC + ' 项）。'
    },
    paretos() { return this.pick(this.a, 'paretos', []) },
    curPareto() { return this.paretos.find(g => g.dim === this.paretoDim) || { rows: [], criticalCount: 0, dimName: '项目' } },
    paretoRows() { return this.pick(this.curPareto, 'rows', []).slice(0, 15) },
    heats() { return this.pick(this.a, 'heats', []) },
    curHeat() { return this.heats.find(g => g.dim === this.heatDim) || { rows: [], dimName: '分公司' } },
    heatMax() {
      let mx = 0;
      (this.curHeat.rows || []).forEach(r => DOMAIN_COLS.forEach(d => { if (r[d.key] > mx) mx = r[d.key] }))
      return Math.max(1, mx)
    },
    repeats() { return this.pick(this.a, 'repeats', []) },
    pairs() { return this.pick(this.a, 'pairs', []) },
    conclusions() {
      const d = this.a
      if (!d) return []
      const list = []
      const pct = n => d.totalCount ? Math.round(n / d.totalCount * 1000) / 10 : 0
      const pr = (this.paretos.find(g => g.dim === 'project') || {}).rows || []
      if (pr.length) list.push('「' + pr[0].label + '」预警最多（' + pr[0].count + ' 项，占 ' + pct(pr[0].count) + '%），为风险集中项目。')
      const doms = (d.trendDomains || [])
      if (doms.length) {
        const top = doms.map(s => ({ name: s.name, n: (s.data || []).reduce((x, y) => x + y, 0) })).sort((x, y) => y.n - x.n)[0]
        if (top && top.n > 0) list.push('风险域以「' + top.name + '」占比最高（' + pct(top.n) + '%），需重点跟踪该类业务口径。')
      }
      if (this.pairs.length) list.push('「' + this.pairs[0].nameA + '」与「' + this.pairs[0].nameB + '」共现于 ' + this.pairs[0].projCount + ' 个项目（' + this.pairs[0].pct + '%），提示同一批项目存在关联性风险（如资金链问题）。')
      if (this.repeats.length) list.push('「' + this.repeats[0].label + '」近 12 个月反复触发 ' + this.repeats[0].count + ' 次，属顽疾对象，建议专项治理。')
      list.push('全量风险指数（Σ 预警值）为 ' + d.riskIndex + '，未闭环率 ' + d.openRate + '%，红色预警需优先处置。')
      return list.slice(0, 5)
    }
  },
  mounted() { this.loadAnalysis() },
  beforeDestroy() { if (this.trendChart) { this.trendChart.dispose(); this.trendChart = null } window.removeEventListener('resize', this.resize) },
  methods: {
    pick(o, k, d) { return o && o[k] !== undefined && o[k] !== null ? o[k] : d },
    curMonthLabel() { const m = this.pick(this.a, 'curMonth', ''); return m ? (m.slice(0, 4) + '-' + m.slice(4)) : '' },
    async loadAnalysis() {
      const r = await xz.EarlyWarnGet('GetRiskAnalysis')
      if (!r || r.status !== 'ok' || !r.data) return
      this.a = r.data
      this.$nextTick(() => this.renderTrend())
      window.removeEventListener('resize', this.resize)
      window.addEventListener('resize', this.resize)
    },
    resize() { if (this.trendChart) this.trendChart.resize() },
    monthLabel(m) { return m ? (m.slice(4, 6) + '月') : '' },
    hbarW(r) {
      const rows = this.paretoRows
      const mx = rows.length ? Math.max(1, rows[0].count) : 1
      return Math.max(2, r.count / mx * 100)
    },
    heatCellStyle(v, mx) {
      if (!v) return { background: '#FAFBFF', color: '#C3C9DE' }
      const alpha = 0.08 + 0.67 * Math.min(1, v / mx)
      return { background: 'rgba(124,92,255,' + alpha.toFixed(2) + ')', color: alpha > 0.45 ? '#fff' : '#4A3D8F', fontWeight: 700 }
    },
    renderTrend() {
      if (!this.$refs.trendChart) return
      if (!this.trendChart) this.trendChart = echarts.init(this.$refs.trendChart)
      const months = (this.pick(this.a, 'months', []) || []).map(this.monthLabel)
      const series = [{ name: '全部预警', type: 'line', smooth: true, symbolSize: 5,
        data: this.pick(this.a, 'trendTotal', []), itemStyle: { color: '#7C5CFF' }, lineStyle: { width: 3, color: '#7C5CFF' } }]
      ;(this.pick(this.a, 'trendDomains', []) || []).forEach(s => {
        series.push({ name: s.name, type: 'line', smooth: true, symbolSize: 4, data: s.data, itemStyle: { color: s.color } })
      })
      this.trendChart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { top: 0, textStyle: { fontSize: 11, color: '#6B7392' } },
        grid: { left: 42, right: 20, top: 34, bottom: 26 },
        xAxis: { type: 'category', data: months, axisLine: { lineStyle: { color: '#D8DEEF' } }, axisLabel: { color: '#6B7392', fontSize: 11 } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#EEF1FA' } }, axisLabel: { color: '#6B7392', fontSize: 11 } },
        series
      })
    }
  }
}
</script>

<style scoped>
.anaPage { padding: 20px 24px 60px; background: #F3F5FC; min-height: 100%; }
.riskHead h2 { font-size: 26px; font-weight: 800; color: #141A33; margin: 0 0 4px; display: flex; align-items: center; }
.riskHead h2 em { font-size: 12px; font-style: normal; color: #fff; border-radius: 6px; padding: 3px 8px; margin-left: 10px; }
.riskSub { font-size: 12.5px; color: #6B7392; margin: 0 0 14px; }
.stripRow { display: grid; grid-template-columns: repeat(8, 1fr); gap: 12px; margin-bottom: 14px; }
.stripCard { background: #fff; border: 1px solid #E9EDF8; border-radius: 12px; box-shadow: 0 3px 14px rgba(30,40,90,.06); padding: 12px 14px; }
.stripCard .k { font-size: 12.5px; color: #6B7392; font-weight: 700; }
.stripCard .v { font-size: 26px; font-weight: 900; line-height: 1.25; letter-spacing: -1px; }
.stripCard .v small { font-size: 12px; font-weight: 700; margin-left: 2px; }
.stripCard .d { font-size: 11.5px; color: #6B7392; }
.riskCard { background: #fff; border: 1px solid #E9EDF8; border-radius: 12px; box-shadow: 0 3px 14px rgba(30,40,90,.06); padding: 16px 18px; margin-bottom: 14px; }
.cardHead { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.cardHead h3 { font-size: 16px; font-weight: 800; color: #141A33; margin: 0; }
.badgeTag { font-size: 11.5px; padding: 2px 9px; border-radius: 10px; font-weight: 700; }
.momText { font-size: 13px; color: #4A5170; margin-bottom: 4px; }
.momText :deep(b) { color: #7C5CFF; }
.tipText { font-size: 12px; color: #8A90AC; }
.tabRow { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
.dimTab { font-size: 13px; color: #6B7392; padding: 5px 14px; border-radius: 16px; cursor: pointer; border: 1px solid transparent; user-select: none; }
.dimTab:hover { background: #F3F5FC; color: #141A33; }
.dimTab.active { background: rgba(124,92,255,.12); border-color: rgba(124,92,255,.4); color: #7C5CFF; font-weight: 700; }
.hbarList { display: flex; flex-direction: column; gap: 7px; }
.hbarRow { display: flex; align-items: center; gap: 10px; }
.hbarLabel { width: 200px; font-size: 12.5px; color: #141A33; text-align: right; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex-shrink: 0; }
.hbarTrack { flex: 1; height: 16px; background: #F3F5FC; border-radius: 8px; overflow: hidden; }
.hbarFill { height: 100%; border-radius: 8px; transition: width .3s; }
.hbarVal { width: 170px; font-size: 12px; color: #6B7392; flex-shrink: 0; }
.tableWrap { overflow-x: auto; }
.heatTable { border-collapse: collapse; width: 100%; font-size: 12.5px; }
.heatTable th { background: #F7F8FD; color: #4A5170; padding: 8px 10px; border: 1px solid #EEF1FA; font-weight: 700; white-space: nowrap; }
.heatTable td { border: 1px solid #EEF1FA; padding: 7px 10px; text-align: center; }
.heatLabel { text-align: left !important; font-weight: 600; color: #141A33; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.heatCell { min-width: 64px; cursor: default; }
.heatTotal { font-weight: 800; color: #141A33; background: #F7F8FD; }
.lgCell { display: inline-block; width: 16px; height: 12px; vertical-align: middle; border-radius: 2px; }
.subTitle { font-size: 14px; font-weight: 800; color: #141A33; margin: 14px 0 8px; }
.pairList { display: flex; flex-direction: column; gap: 6px; }
.pairRow { display: flex; justify-content: space-between; gap: 10px; font-size: 12.5px; color: #4A5170; }
.pairRow :deep(b) { color: #7C5CFF; }
.conclList { margin: 0; padding-left: 22px; line-height: 1.9; font-size: 13.5px; color: #141A33; }
.conclList :deep(b) { color: #7C5CFF; }
.emptyText { font-size: 12.5px; color: #9AA1BC; padding: 18px 0; text-align: center; }
@media (max-width: 1280px) { .stripRow { grid-template-columns: repeat(4, 1fr); } }
@media (max-width: 760px) { .stripRow { grid-template-columns: repeat(2, 1fr); } }
</style>
